<template>
  <div class="flex flex-col gap-3 text-left" :data-testid="`${testidPrefix}-evidence-panel`">
    <!-- Carregando o plantão + arquivos -->
    <div
      v-if="isLoading"
      class="w-full min-h-[500px] rounded-lg bg-lightGray30 animate-pulse"
      :data-testid="`${testidPrefix}-evidence-skeleton`"
    />

    <!-- Falha em achar o registro / carregar o plantão (não é 404 de arquivo) -->
    <div
      v-else-if="loadError"
      class="w-full min-h-[300px] flex flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 p-6 text-center"
      :data-testid="`${testidPrefix}-evidence-error`"
    >
      <svg class="w-10 h-10 text-negative" viewBox="0 0 24 24" aria-hidden="true">
        <path :d="mdiAlertCircleOutline" fill="currentColor" />
      </svg>
      <p class="text-sm text-textPrimary">{{ loadError }}</p>
      <OutlineButton
        text="Tentar novamente"
        width="200px"
        :data-testid="`${testidPrefix}-evidence-retry-button`"
        @click="load"
      />
    </div>

    <!-- Nenhum arquivo disponível: lista vazia ou todos com 404 -->
    <div
      v-else-if="allUnavailable"
      class="w-full min-h-[300px] flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center"
      :data-testid="`${testidPrefix}-evidence-unavailable`"
    >
      <svg class="w-10 h-10 text-neutral50" viewBox="0 0 24 24" aria-hidden="true">
        <path :d="mdiImageOffOutline" fill="currentColor" />
      </svg>
      <p class="font-bold text-textPrimary">Evidência indisponível</p>
      <p class="text-sm text-neutral50 max-w-sm">
        {{
          files.length === 0
            ? 'Este registro não tem arquivo de evidência anexado.'
            : 'O arquivo da evidência não foi encontrado no armazenamento. Tente novamente ou reprove o registro pedindo a redigitação.'
        }}
      </p>
      <OutlineButton
        v-if="files.length > 0"
        text="Tentar novamente"
        width="200px"
        :data-testid="`${testidPrefix}-evidence-retry-button`"
        @click="load"
      />
    </div>

    <template v-else>
      <!-- Strip de arquivos: um botão por evidência, rotulado pelo tipo de evento -->
      <div v-if="files.length > 1" class="flex flex-wrap gap-2" role="tablist">
        <button
          v-for="(file, index) in files"
          :key="file.id"
          type="button"
          role="tab"
          class="flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors"
          :class="
            index === selectedIndex
              ? 'bg-primary30 border-primary30 text-white font-semibold'
              : 'border-gray-300 text-textPrimary hover:bg-gray-100'
          "
          :aria-selected="index === selectedIndex"
          :data-testid="`${testidPrefix}-evidence-file-${file.id}-button`"
          @click="selectFile(index)"
        >
          <svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
            <path
              :d="file.status === 'unavailable' ? mdiImageOffOutline : mdiFileOutline"
              fill="currentColor"
            />
          </svg>
          <span class="truncate max-w-[160px]">{{ fileLabel(file) }}</span>
        </button>
      </div>

      <template v-if="currentFile">
        <StepPreview
          v-if="currentFile.status === 'ready' && currentFile.url"
          :file="null"
          :file-url="currentFile.url"
          :file-type="currentFile.type"
          :rotation="rotation"
          :zoom="zoom"
          mode="preview"
          :show-display-name="false"
          @update:rotation="rotation = $event"
          @update:zoom="zoom = $event"
        />

        <div
          v-else
          class="w-full min-h-[300px] flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center"
          :data-testid="`${testidPrefix}-evidence-file-unavailable`"
        >
          <svg class="w-10 h-10 text-neutral50" viewBox="0 0 24 24" aria-hidden="true">
            <path :d="mdiImageOffOutline" fill="currentColor" />
          </svg>
          <p class="font-bold text-textPrimary">
            {{
              currentFile.status === 'unavailable'
                ? 'Arquivo indisponível'
                : 'Falha ao baixar o arquivo'
            }}
          </p>
          <p v-if="currentFile.error" class="text-sm text-neutral50">{{ currentFile.error }}</p>
          <OutlineButton
            text="Tentar novamente"
            width="200px"
            :data-testid="`${testidPrefix}-evidence-retry-button`"
            @click="load"
          />
        </div>

        <p class="text-xs text-neutral50 truncate" :title="currentFile.name">
          {{ fileLabel(currentFile) }} · {{ currentFile.name }}
        </p>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { mdiAlertCircleOutline, mdiFileOutline, mdiImageOffOutline } from '@mdi/js'

import Network from '@/Network'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'
import type { CheckValidationDetailResponse } from '@/Network/Types/Responses/CheckValidationResponses'
import { CHECK_EVIDENCE_EVENT_TYPE_LABELS } from '@/constants/check-validation.constants'
import { getApiErrorMessage } from '@/utils/apiError'

import { isEvidenceUnavailableError } from '../errors'

export interface CheckEvidenceContext {
  checkin: string | null
  checkout: string | null
  timezone: string
  doctorName: string | null
  crm: { number: string; uf: string } | null
}

type EvidenceFileStatus = 'loading' | 'ready' | 'unavailable' | 'error'

interface EvidenceFile {
  id: number
  name: string
  eventType: string | null
  url: string | null
  type: string
  status: EvidenceFileStatus
  error: string | null
}

const props = withDefaults(
  defineProps<{
    checkId: number
    testidPrefix?: string
  }>(),
  { testidPrefix: 'check-validation' }
)

const emit = defineEmits<{
  loaded: [context: CheckEvidenceContext]
  // Nenhum arquivo exibível (lista vazia ou todos com 404) — o pai marca a linha.
  unavailable: []
  // Pelo menos um arquivo exibível — o pai limpa o marcador se ele existia.
  available: []
}>()

const isLoading = ref(false)
const loadError = ref<string | null>(null)
const files = ref<EvidenceFile[]>([])
const selectedIndex = ref(0)
const rotation = ref(0)
const zoom = ref(1)

const currentFile = computed(() => files.value[selectedIndex.value] ?? null)

const allUnavailable = computed(
  () => files.value.length === 0 || files.value.every((file) => file.status === 'unavailable')
)

const fileLabel = (file: EvidenceFile): string =>
  (file.eventType && CHECK_EVIDENCE_EVENT_TYPE_LABELS[file.eventType]) || file.name

// Blob URLs são revogadas ao trocar de registro e ao desmontar — sem isso cada
// abertura do modal vazaria a memória do arquivo (DF-1 do EvidenceModal).
function revokeAll() {
  for (const file of files.value) {
    if (file.url) URL.revokeObjectURL(file.url)
  }
}

function selectFile(index: number) {
  selectedIndex.value = index
  rotation.value = 0
  zoom.value = 1
}

async function downloadOne(file: EvidenceFile, stationId: number) {
  try {
    const blob = await Network.doctorCheckEvidence.downloadCheckEvidenceFile(stationId, file.id)
    file.url = URL.createObjectURL(blob)
    file.type = blob.type
    file.status = 'ready'
  } catch (error) {
    console.error(error)
    if (isEvidenceUnavailableError(error)) {
      file.status = 'unavailable'
    } else {
      file.status = 'error'
      file.error = getApiErrorMessage(error, 'Não foi possível baixar a evidência')
    }
  }
}

function contextFrom(detail: CheckValidationDetailResponse): CheckEvidenceContext {
  const { doctor } = detail
  return {
    checkin: detail.checkin,
    checkout: detail.checkout,
    timezone: detail.station.timezone || '',
    doctorName: doctor.name,
    crm: doctor.crm ? { number: doctor.crm, uf: doctor.crmUf ?? '' } : null
  }
}

async function load() {
  const { checkId } = props
  isLoading.value = true
  loadError.value = null
  revokeAll()
  files.value = []
  selectedIndex.value = 0

  try {
    const detail = await Network.checkValidation.getDetail(checkId)
    if (checkId !== props.checkId) return

    emit('loaded', contextFrom(detail))

    files.value = detail.evidences.map((evidence) => ({
      id: evidence.id,
      name: evidence.name ?? `evidencia-${evidence.id}`,
      eventType: evidence.eventType,
      url: null,
      type: '',
      status: 'loading' as EvidenceFileStatus,
      error: null
    }))

    await Promise.all(files.value.map((file) => downloadOne(file, detail.station.id)))
    if (checkId !== props.checkId) return

    const firstReady = files.value.findIndex((file) => file.status === 'ready')
    selectedIndex.value = firstReady >= 0 ? firstReady : 0

    if (allUnavailable.value) emit('unavailable')
    else emit('available')
  } catch (error) {
    console.error(error)
    if (checkId === props.checkId) {
      loadError.value = getApiErrorMessage(
        error,
        'Não foi possível carregar as evidências do registro'
      )
    }
  } finally {
    if (checkId === props.checkId) isLoading.value = false
  }
}

watch(
  () => props.checkId,
  () => void load(),
  { immediate: true }
)

onBeforeUnmount(revokeAll)

defineExpose({ load })
</script>
